import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import {
  doUpsertMonthPreferences,
  type U3400KpiUserLuPreferenceEffective,
} from '../../u3400Dashboard/U3400Service';
import {
  doFetchMonthDetail,
  doFetchStandaloneSeries,
  parseMonthDetail,
  parseStandaloneSeries,
} from '../api/u340xKpiStandaloneApi';
import type { U340xKpiLogicalUnitCode } from '../types/u340xKpiLuTypes';
import { NS as U340X_KPI_NS } from '../u340xKpiNs';

type LuHandleError = (
  t: (key: string, options?: Record<string, unknown>) => string,
  nameSpace: string,
  err: unknown,
  errorResult: U2000ApiErrorResult,
) => U2000ApiErrorResult;

export interface UseU340xKpiStandaloneHandlersParams {
  logicalUnitCode: U340xKpiLogicalUnitCode;
  nameSpace: string;
  luHandleAppiError: LuHandleError;
}

export function useU340xKpiStandaloneHandlers({
  logicalUnitCode,
  nameSpace,
  luHandleAppiError,
}: UseU340xKpiStandaloneHandlersParams) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation(nameSpace);
  const { t: tKpi } = useTranslation(U340X_KPI_NS);
  const toast = useToast();

  const handleError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(
        error as Error,
        t,
        nameSpace,
        luHandleAppiError,
      ) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
    [luHandleAppiError, nameSpace, t, toast],
  );

  const fetchStandaloneSeries = useCallback(
    async (months: number, anchorMonth: string) => {
      const response = await doFetchStandaloneSeries(
        axios,
        logicalUnitCode,
        months,
        anchorMonth,
      );
      return parseStandaloneSeries(response.data?.result);
    },
    [axios, logicalUnitCode],
  );

  const fetchMonthDetail = useCallback(
    async (month: string) => {
      const response = await doFetchMonthDetail(axios, logicalUnitCode, month);
      return parseMonthDetail(logicalUnitCode, response.data?.result);
    },
    [axios, logicalUnitCode],
  );

  const upsertMonthsStandalone = useCallback(
    async (monthsStandalone: number) => {
      try {
        const response = await doUpsertMonthPreferences(axios, {
          logicalUnitCode,
          monthsStandalone,
        });
        const next = response.data?.result as U3400KpiUserLuPreferenceEffective;
        toast.success(tKpi('toast.monthsSaved'));
        return next;
      } catch (error: unknown) {
        handleError(error);
        return null;
      }
    },
    [axios, handleError, logicalUnitCode, tKpi, toast],
  );

  return {
    handleError,
    fetchStandaloneSeries,
    fetchMonthDetail,
    upsertMonthsStandalone,
  };
}
